import { Dispatch, FC, RefObject, SetStateAction } from "react";
import toast from "react-hot-toast";

export type ProofFile = { file: File; url: string };

/**
 * Proof of insurance, for the package that asks for it before payment: the
 * same upload and rules as the desktop section (1NOW-1232) — images and PDFs
 * only, a video is refused with the same sentence — drawn for a phone.
 */
const ProofUpload: FC<{
  files: ProofFile[];
  setFiles: Dispatch<SetStateAction<ProofFile[]>>;
  inputRef: RefObject<HTMLInputElement>;
}> = ({ files, setFiles, inputRef }) => (
  <div className="mt-3 w-full">
    {files.length > 0 && (
      <ul className="mb-3 flex flex-col gap-2">
        {files.map(({ file, url }, index) => (
          <li key={index} className="flex items-center gap-3 rounded-xl border border-secondary-100 p-2">
            {file.type.startsWith("image/") ? (
              <img src={url} alt="Proof of insurance preview" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
            ) : (
              <span className="flex h-14 w-20 shrink-0 items-center justify-center rounded-lg bg-secondary-100/50 text-xs font-bold">
                PDF
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{file.name}</p>
              <div className="mt-1 flex gap-3 text-xs font-semibold">
                <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
                  {file.type.startsWith("image/") ? "Preview" : "Open"}
                </a>
                <button
                  type="button"
                  className="text-red-500"
                  onClick={() => {
                    URL.revokeObjectURL(url);
                    setFiles((prev) => prev.filter((_, i) => i !== index));
                    if (inputRef.current) inputRef.current.value = "";
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    )}
    <input
      ref={inputRef}
      type="file"
      style={{ display: "none" }}
      accept="image/*,application/pdf"
      multiple
      onChange={(e) => {
        const picked = e.target.files ? Array.from(e.target.files) : [];
        // A document, so a video (or anything else) is refused and the renter told why.
        const allowed = picked.filter((f) => f.type.startsWith("image/") || f.type === "application/pdf");
        if (allowed.length < picked.length) toast.error("Videos aren't supported. Please upload an image or PDF.");
        if (allowed.length > 0)
          setFiles((prev) => [...prev, ...allowed.map((f) => ({ file: f, url: URL.createObjectURL(f) }))]);
        e.target.value = "";
      }}
    />
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      className="flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-secondary-100 p-5 text-sm"
    >
      <span className="font-bold">Upload proof of insurance</span>
      <span className="text-xs opacity-70">Photo or PDF of your insurance card</span>
    </button>
  </div>
);

export default ProofUpload;
